/* XYLO Copilot — le navigateur comme paire de mains de Claude.
   1. snapshotPage() lit la page en texte : chaque élément utile reçoit un ref
      [c12] (bouton, lien, champ avec sa valeur, case, onglet, ligne de tableau,
      option de liste), les dialogues et notifications sont signalés.
   2. executeAction() exécute ce que Claude demande (navigate, click, type,
      select_option, press_key, scroll, wait, read_page) avec un curseur fantôme
      et une surbrillance, pour que l'utilisateur voie le logiciel bouger.
   Rien ici n'écrit en base : seulement des clics et des saisies dans l'interface,
   avec la session de l'utilisateur. */
import navMenuItems from '@/layouts/components/vertical-nav-menu/navMenuItems.js'

// Ce que la lecture ignore : le panneau lui-même, le menu, la barre, le pied de page.
const EXCLUDE_SEL = '#cp-root, .parentx, header.vs-navbar, .vx-navbar, .the-footer, .call-float, .customizer, .vs-notifications'
const SKIP_TAGS = { script: 1, style: 1, svg: 1, noscript: 1, template: 1, link: 1, meta: 1, br: 1, hr: 1 }
// Étiquettes déjà portées par le champ qu'elles accompagnent (pas de doublon dans la lecture).
const LABEL_SEL = '.vs-input--label, .vs-placeholder-label, .vs-select--label'
const INTERACTIVE_SEL = 'a[href], button, input, textarea, select, [role="button"], .v-select, .vs-select--item, .vs__dropdown-option, .cursor-pointer, [onclick], table, .ag-root-wrapper'
const MAX_CHARS = 14000
const MAX_TABLE_ROWS = 30
const MAX_SELECT_OPTIONS = 40

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))
const norm = s => (s || '').replace(/\s+/g, ' ').trim()
const fold = s => norm(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
function clipText (s, n) { return s.length > n ? `${s.slice(0, n)}…` : s }

let refs = new Map()      // ref -> element (lecture courante)
let refLabels = new Map() // ref -> libellé lisible (pour la ligne d'état du panneau)
let counter = 0

// ── Lecture de la page ───────────────────────────────────────────────────────

function isVisible (el) {
  if (!el || el.nodeType !== 1) return false
  if (!el.getClientRects().length) return false
  const cs = getComputedStyle(el)
  if (cs.visibility === 'hidden') return false
  // Les cases Vuesax sont des <input> à opacité 0 posés sur leur étiquette : on les garde.
  if (cs.opacity === '0' && !(el.tagName === 'INPUT' && /checkbox|radio/.test(el.type))) return false
  return true
}

function newRef (el, label) {
  counter += 1
  const ref = `c${counter}`
  el.setAttribute('data-cp-ref', ref)
  refs.set(ref, el)
  refLabels.set(ref, label)
  return ref
}

function iconName (el) {
  const svg = el.querySelector('svg.feather, svg[class*="feather-"]')
  if (!svg) return ''
  const m = (svg.getAttribute('class') || '').match(/feather-([a-z0-9-]+)/)
  return m ? `icon:${m[1]}` : ''
}

function ownText (el) {
  // Texte de l'élément sans ses descendants interactifs (déjà listés à part).
  const clone = el.cloneNode(true)
  clone.querySelectorAll('input, textarea, select, svg').forEach(n => n.remove())
  return norm(clone.textContent)
}

function labelFor (el) {
  const aria = el.getAttribute('aria-label') || el.getAttribute('title')
  if (aria) return norm(aria)
  const vs = el.closest('.vs-con-input-label, .vs-con-textarea, .vs-component')
  if (vs) {
    const l = vs.querySelector('.vs-input--label, .vs-placeholder-label, .vs-select--label, h4')
    if (l && norm(l.textContent)) return norm(l.textContent)
  }
  if (el.placeholder) return norm(el.placeholder)
  if (el.id) {
    const l = document.querySelector(`label[for="${el.id}"]`)
    if (l) return norm(l.textContent)
  }
  const wrap = el.closest('label')
  if (wrap) return ownText(wrap)
  // Étiquette posée juste avant le champ (ou avant son conteneur).
  let node = el
  for (let i = 0; i < 3 && node; i++) {
    let prev = node.previousElementSibling
    while (prev && !norm(prev.textContent)) prev = prev.previousElementSibling
    if (prev && /^(LABEL|SPAN|SMALL|H[1-6]|P|DIV|STRONG|B)$/.test(prev.tagName) && !prev.querySelector(INTERACTIVE_SEL)) {
      const t = norm(prev.textContent)
      if (t && t.length < 60) return t
    }
    node = node.parentElement
  }
  return el.name || ''
}

function q (s) { return `"${clipText(norm(s), 80)}"` }

// Décrit un élément interactif sur une ligne, avec son ref. Renvoie '' si rien à dire.
function describeInteractive (el) {
  const tag = el.tagName.toLowerCase()
  if (el.classList.contains('v-select')) {
    const value = Array.from(el.querySelectorAll('.vs__selected')).map(n => norm(n.textContent)).filter(Boolean).join(', ')
    const label = labelFor(el)
    const ref = newRef(el, `combobox ${label}`)
    let line = `[${ref}] combobox ${q(label)} = ${q(value)} (type to search, then pick an option)`
    const menu = el.querySelector('.vs__dropdown-menu')
    if (menu && isVisible(menu)) {
      const opts = Array.from(menu.querySelectorAll('.vs__dropdown-option')).filter(isVisible)
      opts.slice(0, MAX_SELECT_OPTIONS).forEach(o => { line += `\n    [${newRef(o, `option ${norm(o.textContent)}`)}] option ${q(o.textContent)}` })
      if (opts.length > MAX_SELECT_OPTIONS) line += `\n    … ${opts.length - MAX_SELECT_OPTIONS} more options (type to filter)`
      if (!opts.length) line += '\n    (no option yet — type to search)'
    }
    return line
  }
  if (tag === 'input') {
    const type = (el.type || 'text').toLowerCase()
    if (type === 'hidden') return ''
    const label = labelFor(el)
    if (type === 'checkbox' || type === 'radio') {
      const ref = newRef(el, `${type} ${label}`)
      return `[${ref}] ${type} ${q(label)} ${el.checked ? '[x]' : '[ ]'}${el.disabled ? ' (disabled)' : ''}`
    }
    if (el.classList.contains('vs-select--input')) {
      const ref = newRef(el, `select ${label}`)
      return `[${ref}] select ${q(label)} = ${q(el.value)} (click to open)${el.disabled ? ' (disabled)' : ''}`
    }
    if (el.classList.contains('flatpickr-input') || el._flatpickr) {
      const ref = newRef(el, `date ${label}`)
      return `[${ref}] date picker ${q(label)} = ${q(el.value)} (select_option with YYYY-MM-DD)`
    }
    if (type === 'file') return `[${newRef(el, `file ${label}`)}] file input ${q(label)}`
    if (type === 'submit' || type === 'button') return `[${newRef(el, `button ${el.value}`)}] button ${q(el.value)}`
    const ref = newRef(el, `input ${label}`)
    const value = type === 'password' ? (el.value ? '••••' : '') : el.value
    return `[${ref}] input ${q(label)} = ${q(value)}${el.disabled || el.readOnly ? ' (read-only)' : ''}`
  }
  if (tag === 'textarea') {
    const label = labelFor(el)
    return `[${newRef(el, `textarea ${label}`)}] textarea ${q(label)} = ${q(clipText(el.value, 200))}`
  }
  if (tag === 'select') {
    const label = labelFor(el)
    const sel = el.options[el.selectedIndex]
    const opts = Array.from(el.options).map(o => norm(o.textContent)).filter(Boolean)
    const shown = opts.slice(0, MAX_SELECT_OPTIONS).join(' | ') + (opts.length > MAX_SELECT_OPTIONS ? ` | … (${opts.length} options)` : '')
    return `[${newRef(el, `select ${label}`)}] select ${q(label)} = ${q(sel ? sel.textContent : '')} options: ${shown}`
  }
  if (el.classList.contains('vs-select--item')) {
    return `[${newRef(el, `option ${norm(el.textContent)}`)}] option ${q(el.textContent)}${el.classList.contains('activex') ? ' (selected)' : ''}`
  }
  if (el.classList.contains('vs__dropdown-option')) {
    return `[${newRef(el, `option ${norm(el.textContent)}`)}] option ${q(el.textContent)}`
  }
  if (tag === 'a') {
    const text = ownText(el) || iconName(el) || norm(el.getAttribute('title'))
    if (!text) return ''
    const href = (el.getAttribute('href') || '').replace(/^\/web/, '')
    return `[${newRef(el, `link ${text}`)}] link ${q(text)}${href && href !== '#' && !href.startsWith('javascript') ? ` → ${href}` : ''}`
  }
  if (tag === 'button' || el.getAttribute('role') === 'button') {
    const text = ownText(el) || iconName(el) || norm(el.getAttribute('title') || el.getAttribute('aria-label'))
    if (!text) return ''
    const isTab = el.classList.contains('vs-tabs--btn')
    const li = isTab ? el.closest('li') : null
    const active = li && li.classList.contains('activeChild') ? ' (active)' : ''
    return `[${newRef(el, `${isTab ? 'tab' : 'button'} ${text}`)}] ${isTab ? 'tab' : 'button'} ${q(text)}${active}${el.disabled ? ' (disabled)' : ''}`
  }
  // Élément cliquable sans sémantique (carte, icône, ligne) : seulement s'il est petit.
  const text = ownText(el) || iconName(el)
  if (!text || text.length > 120) return ''
  return `[${newRef(el, `clickable ${text}`)}] clickable ${q(text)}`
}

function isInteractive (el) {
  if (el.matches(INTERACTIVE_SEL)) return true
  if (/^(DIV|SPAN|LI|TD|I|IMG|P)$/.test(el.tagName) && el.childElementCount <= 3) {
    return getComputedStyle(el).cursor === 'pointer'
  }
  return false
}

// Contenu d'une cellule / d'un item, avec les refs des éléments cliquables en ligne.
function inlineContent (el) {
  const parts = []
  const visit = node => {
    if (node.nodeType === 3) { const t = norm(node.nodeValue); if (t) parts.push(t); return }
    if (node.nodeType !== 1 || SKIP_TAGS[node.tagName.toLowerCase()] || !isVisible(node)) return
    if (isInteractive(node) && !node.matches('table, .ag-root-wrapper')) {
      const d = describeInteractive(node)
      if (d) parts.push(d.replace(/\n\s+/g, ' '))
      return
    }
    node.childNodes.forEach(visit)
  }
  visit(el)
  return parts.join(' ')
}

function emitTable (table, lines) {
  const heads = Array.from(table.querySelectorAll('thead th, thead td')).filter(isVisible).map(h => norm(h.textContent))
  if (heads.length) lines.push(`TABLE | ${heads.join(' | ')} |`)
  else lines.push('TABLE')
  const rows = Array.from(table.querySelectorAll('tbody tr, tr.tr-values')).filter(r => isVisible(r) && !r.closest('thead'))
  rows.slice(0, MAX_TABLE_ROWS).forEach(r => {
    const cells = Array.from(r.children).filter(isVisible).map(c => inlineContent(c))
    if (!cells.join('').trim()) return
    const ref = newRef(r, `row ${clipText(cells.join(' '), 40)}`)
    lines.push(`  [${ref}] row | ${cells.join(' | ')} |`)
  })
  if (rows.length > MAX_TABLE_ROWS) lines.push(`  … ${rows.length - MAX_TABLE_ROWS} more rows (use read_page with a query, or the search box)`)
}

function emitAgGrid (root, lines) {
  const heads = Array.from(root.querySelectorAll('.ag-header-cell')).filter(isVisible)
    .map(h => norm(h.textContent)).filter(Boolean)
  lines.push(`GRID | ${heads.join(' | ')} |`)
  const byIndex = new Map()
  root.querySelectorAll('.ag-row').forEach(r => {
    const idx = Number(r.getAttribute('row-index'))
    if (!byIndex.has(idx)) byIndex.set(idx, [])
    byIndex.get(idx).push(r)
  })
  const indexes = Array.from(byIndex.keys()).sort((a, b) => a - b)
  indexes.slice(0, MAX_TABLE_ROWS).forEach(idx => {
    const parts = byIndex.get(idx)
    const cells = []
    parts.forEach(p => p.querySelectorAll('.ag-cell').forEach(c => cells.push(inlineContent(c))))
    const main = parts.find(p => p.closest('.ag-center-cols-container')) || parts[0]
    const ref = newRef(main, `row ${clipText(cells.join(' '), 40)}`)
    lines.push(`  [${ref}] row | ${cells.join(' | ')} |`)
  })
  if (indexes.length > MAX_TABLE_ROWS) lines.push(`  … ${indexes.length - MAX_TABLE_ROWS} more rendered rows`)
  const paging = root.querySelector('.ag-paging-panel')
  if (paging && isVisible(paging)) lines.push(`  paging: ${inlineContent(paging)}`)
  const search = root.parentElement && root.parentElement.querySelector('input[type="search"], input[placeholder*="earch"]')
  if (search && isVisible(search) && !search.getAttribute('data-cp-ref')) lines.push(describeInteractive(search))
}

function walk (el, lines, depth) {
  if (el.nodeType !== 1) return
  const tag = el.tagName.toLowerCase()
  if (SKIP_TAGS[tag] || el.matches(EXCLUDE_SEL) || el.matches(LABEL_SEL) || !isVisible(el)) return
  if (el.classList.contains('vs-popup')) {
    const title = el.querySelector('.vs-popup--title')
    lines.push(`\nDIALOG ${q(title ? title.textContent : 'dialog')}`)
    const close = el.querySelector('.vs-popup--close')
    if (close) lines.push(`  [${newRef(close, 'close dialog')}] button "close dialog"`)
    const content = el.querySelector('.vs-popup--content') || el
    content.childNodes.forEach(c => walk(c, lines, depth + 1))
    return
  }
  if (tag === 'table') { emitTable(el, lines); return }
  if (el.classList.contains('ag-root-wrapper')) { emitAgGrid(el, lines); return }
  if (isInteractive(el)) {
    const d = describeInteractive(el)
    if (d) lines.push(d)
    if (el.classList.contains('v-select') || tag === 'button' || tag === 'a') return
    // Un conteneur cliquable (carte) peut contenir des boutons : on continue.
    if (!el.querySelector(INTERACTIVE_SEL)) return
  }
  if (!el.querySelector(INTERACTIVE_SEL)) {
    // Bloc de texte sans élément interactif : une ligne.
    const t = norm(el.textContent)
    if (!t) return
    const prefix = /^h[1-6]$/.test(tag) ? '## ' : (tag === 'label' || tag === 'th' ? '' : '')
    lines.push(prefix + clipText(t, 400))
    return
  }
  el.childNodes.forEach(c => {
    if (c.nodeType === 3) { const t = norm(c.nodeValue); if (t) lines.push(t) } else walk(c, lines, depth + 1)
  })
}

function dedupe (lines) {
  const out = []
  let prev = null
  lines.forEach(l => { if (l !== prev) out.push(l); prev = l })
  return out
}

export function snapshotPage (router) {
  document.querySelectorAll('[data-cp-ref]').forEach(el => el.removeAttribute('data-cp-ref'))
  refs = new Map(); refLabels = new Map(); counter = 0
  const lines = []
  const notes = Array.from(document.querySelectorAll('.vs-notifications .vs-notify')).filter(isVisible)
  notes.forEach(n => lines.push(`NOTIFICATION: ${norm(n.textContent)}`))
  const loading = document.querySelector('.con-vs-loading')
  if (loading && isVisible(loading)) lines.push('LOADING… (use wait)')
  walk(document.body, lines, 0)
  let text = dedupe(lines).join('\n').replace(/\n{3,}/g, '\n\n')
  if (text.length > MAX_CHARS) text = `${text.slice(0, MAX_CHARS)}\n… (page truncated: ${text.length - MAX_CHARS} more characters — use read_page with a query)`
  const route = router && router.currentRoute ? router.currentRoute : null
  return {
    url: route ? route.fullPath : window.location.pathname,
    title: route && route.meta && route.meta.pageTitle ? route.meta.pageTitle : document.title,
    text
  }
}

export function pageToText (page) {
  return `URL: ${page.url}${page.title ? `\nPAGE: ${page.title}` : ''}\n${page.text}`
}

// ── Curseur fantôme et surbrillance ──────────────────────────────────────────

let cursor = null
function ensureCursor () {
  if (cursor && cursor.isConnected) return cursor
  cursor = document.createElement('div')
  cursor.id = 'cp-cursor'
  cursor.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22"><path d="M4 2l16 9-7 2-3 7z" fill="#333b34" stroke="#fff" stroke-width="1.5" stroke-linejoin="round"/></svg><span class="cp-cursor-ring"></span>'
  document.body.appendChild(cursor)
  return cursor
}

async function cursorTo (el) {
  const c = ensureCursor()
  el.scrollIntoView({ block: 'center', inline: 'nearest' })
  await sleep(120)
  const r = el.getBoundingClientRect()
  const x = Math.min(window.innerWidth - 10, r.left + Math.min((r.width / 2), 120))
  const y = r.top + (r.height / 2)
  c.style.opacity = '1'
  c.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`
  await sleep(420)
}

function pulse () {
  const c = ensureCursor()
  c.classList.remove('is-click')
  void c.offsetWidth
  c.classList.add('is-click')
}

export function hideCursor () {
  if (cursor) cursor.style.opacity = '0'
}

function highlight (el) {
  const target = el.closest('.vs-con-input, .vs-con-select, .v-select, .con-vs-checkbox, .vs-switch, .vs-radio') || el
  target.classList.add('cp-highlight')
  setTimeout(() => target.classList.remove('cp-highlight'), 1200)
}

// ── Exécution des actions ────────────────────────────────────────────────────

function resolve (ref) {
  const el = refs.get(ref) || document.querySelector(`[data-cp-ref="${ref}"]`)
  if (!el || !el.isConnected) throw new Error(`Element ${ref} is no longer on the page: read the page again.`)
  return el
}

async function settle () {
  await sleep(450)
  for (let i = 0; i < 32; i++) {
    const spin = document.querySelector('.con-vs-loading')
    if (!spin || !isVisible(spin)) break
    await sleep(250)
  }
}

function fire (el, type, init) {
  const Ctor = /^key/.test(type) ? KeyboardEvent : (/^mouse|^click$/.test(type) ? MouseEvent : Event)
  el.dispatchEvent(new Ctor(type, Object.assign({ bubbles: true, cancelable: true }, init || {})))
}

const KEYS = { Enter: 13, Escape: 27, Tab: 9, ArrowDown: 40, ArrowUp: 38, Backspace: 8, Space: 32 }
function pressKey (el, key) {
  const k = key === ' ' ? 'Space' : key
  const code = KEYS[k] || 0
  const init = { key: k === 'Space' ? ' ' : k, code: k, keyCode: code, which: code }
  fire(el, 'keydown', init); fire(el, 'keypress', init); fire(el, 'keyup', init)
}

function isTextField (el) {
  if (el.tagName === 'TEXTAREA') return true
  return el.tagName === 'INPUT' && !/checkbox|radio|file|submit|button|hidden/.test(el.type || 'text')
}

async function setValue (el, text) {
  const proto = el.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
  const setter = Object.getOwnPropertyDescriptor(proto, 'value').set
  const put = v => { setter.call(el, v); fire(el, 'input') }
  if (el.type === 'number' || text.length > 120) {
    put(text)
  } else {
    const step = Math.max(10, Math.min(30, Math.floor(900 / Math.max(1, text.length))))
    put('')
    for (let i = 1; i <= text.length; i++) { put(text.slice(0, i)); if (i % 2 === 0) await sleep(step) }
  }
  fire(el, 'change')
}

function pickOption (candidates, wanted) {
  const want = fold(wanted)
  const texts = candidates.map(c => fold(c.textContent))
  let i = texts.findIndex(t => t === want)
  if (i < 0) i = texts.findIndex(t => t.startsWith(want))
  if (i < 0) i = texts.findIndex(t => t.includes(want))
  return i >= 0 ? candidates[i] : null
}

async function doClick (el) {
  await cursorTo(el)
  highlight(el)
  pulse()
  if (el.classList.contains('v-select')) {
    const toggle = el.querySelector('.vs__dropdown-toggle') || el
    fire(toggle, 'mousedown'); fire(toggle, 'mouseup')
    const search = el.querySelector('input.vs__search')
    if (search) search.focus()
    return 'Opened the combobox.'
  }
  if (el.tagName === 'INPUT' && el.classList.contains('vs-select--input')) {
    el.focus(); fire(el, 'focus')
    return 'Opened the select.'
  }
  if (el.tagName === 'INPUT' && /checkbox|radio/.test(el.type)) {
    el.click()
    return `Now ${el.checked ? 'checked' : 'unchecked'}.`
  }
  if (typeof el.focus === 'function' && !/^(TR|TD|DIV|SPAN|LI)$/.test(el.tagName)) el.focus()
  fire(el, 'mousedown'); fire(el, 'mouseup')
  el.click()
  return 'Clicked.'
}

async function doType (el, text, submit) {
  if (el.classList.contains('v-select')) {
    const toggle = el.querySelector('.vs__dropdown-toggle') || el
    fire(toggle, 'mousedown'); fire(toggle, 'mouseup')
    el = el.querySelector('input.vs__search')
  }
  if (!el || !isTextField(el)) throw new Error('This element is not a text field. Use click or select_option.')
  if (el.readOnly && !el.classList.contains('vs__search')) throw new Error('This field is read-only (a select or a date picker?): use select_option.')
  await cursorTo(el)
  highlight(el)
  el.focus()
  await setValue(el, String(text))
  if (submit) { await sleep(80); pressKey(el, 'Enter') }
  return `Typed "${clipText(String(text), 60)}".${submit ? ' Pressed Enter.' : ''}`
}

async function doSelect (el, option) {
  const wanted = String(option)
  if (el.tagName === 'SELECT') {
    const opt = pickOption(Array.from(el.options), wanted)
    if (!opt) throw new Error(`No option containing "${wanted}". Options: ${Array.from(el.options).map(o => norm(o.textContent)).join(' | ')}`)
    await cursorTo(el); highlight(el)
    el.value = opt.value
    fire(el, 'change'); fire(el, 'input')
    return `Selected "${norm(opt.textContent)}".`
  }
  if (el._flatpickr) {
    await cursorTo(el); highlight(el)
    el._flatpickr.setDate(wanted, true)
    return `Date set to "${el.value}".`
  }
  const vSelect = el.classList.contains('v-select') ? el : el.closest('.v-select')
  if (vSelect) {
    await doType(vSelect, wanted, false)
    let found = null
    let visible = []
    for (let attempt = 0; attempt < 8 && !found; attempt++) {
      await sleep(350)
      visible = Array.from(vSelect.querySelectorAll('.vs__dropdown-option')).filter(isVisible)
      found = pickOption(visible, wanted)
    }
    if (!found) {
      const seen = visible.map(o => norm(o.textContent)).slice(0, 15).join(' | ')
      throw new Error(`No option containing "${wanted}" in the combobox.${seen ? ` Options shown: ${seen}` : ' No option appeared.'}`)
    }
    await cursorTo(found); pulse()
    found.click()
    await sleep(150)
    const value = Array.from(vSelect.querySelectorAll('.vs__selected')).map(n => norm(n.textContent)).join(', ')
    return `Selected "${norm(found.textContent)}". Combobox now = "${value}".`
  }
  const input = el.classList.contains('vs-select--input') ? el : (el.closest('.vs-component') || el).querySelector('input.vs-select--input')
  if (input) {
    await cursorTo(input); highlight(input)
    input.focus(); fire(input, 'focus')
    let found = null
    for (let attempt = 0; attempt < 4 && !found; attempt++) {
      await sleep(250)
      const root = input.closest('.vs-component') || document
      const items = Array.from(root.querySelectorAll('button.vs-select--item')).filter(isVisible)
      found = pickOption(items, wanted)
      if (!found && attempt === 3) {
        throw new Error(`No option containing "${wanted}". Options: ${items.map(o => norm(o.textContent)).slice(0, 30).join(' | ')}`)
      }
    }
    await cursorTo(found); pulse()
    found.click()
    await sleep(150)
    return `Selected "${norm(found.textContent)}". Select now = "${input.value}".`
  }
  throw new Error('This element is not a dropdown.')
}

export function describeAction (a) {
  const i = a.input || {}
  const label = i.ref ? (refLabels.get(i.ref) || i.ref) : ''
  switch (a.name) {
  case 'navigate': return `Open ${i.path}`
  case 'click': return `Click ${label}`
  case 'type': return `Type “${clipText(String(i.text || ''), 40)}” in ${label}`
  case 'select_option': return `Choose “${i.option}” in ${label}`
  case 'press_key': return `Press ${i.key} on ${label}`
  case 'scroll': return `Scroll ${i.direction}${label ? ` (${label})` : ''}`
  case 'wait': return 'Wait for the page'
  case 'read_page': return i.query ? `Look for “${i.query}” on the page` : 'Read the page'
  default: return a.name
  }
}

// Exécute une action et renvoie { text, error, page } — page = lecture après l'action.
export async function executeAction (a, router) {
  const i = a.input || {}
  let text = ''
  let error = false
  try {
    switch (a.name) {
    case 'navigate': {
      let path = String(i.path || '').trim().replace(/^\/web(?=\/)/, '')
      if (!path.startsWith('/')) path = `/${path}`
      hideCursor()
      if (router.currentRoute.fullPath !== path) {
        await router.push(path).catch(e => { if (!e || !/redundant|duplicated/i.test(e.message || e.name || '')) throw e })
      }
      await settle()
      await sleep(300)
      text = `Now on ${router.currentRoute.fullPath}.`
      break
    }
    case 'click': text = await doClick(resolve(i.ref)); await settle(); break
    case 'type': text = await doType(resolve(i.ref), String(i.text === null || i.text === undefined ? '' : i.text), !!i.submit); await settle(); break
    case 'select_option': text = await doSelect(resolve(i.ref), i.option); await settle(); break
    case 'press_key': {
      const el = resolve(i.ref)
      await cursorTo(el); highlight(el); el.focus()
      pressKey(el, String(i.key || 'Enter'))
      await settle()
      text = `Pressed ${i.key}.`
      break
    }
    case 'scroll': {
      if (i.ref) {
        const el = resolve(i.ref)
        el.scrollIntoView({ block: i.direction === 'into_view' ? 'center' : (i.direction === 'up' ? 'start' : 'end') })
        await cursorTo(el)
      } else {
        window.scrollBy({ top: (i.direction === 'up' ? -1 : 1) * window.innerHeight * 0.8, behavior: 'smooth' })
        await sleep(500)
      }
      text = 'Scrolled.'
      break
    }
    case 'wait': {
      const ms = Math.min(8000, Math.max(200, Number(i.ms) || 1000))
      await sleep(ms); await settle()
      text = `Waited ${ms} ms.`
      break
    }
    case 'read_page': {
      const page = snapshotPage(router)
      if (i.query) {
        const want = fold(i.query)
        const hits = page.text.split('\n').filter(l => fold(l).includes(want))
        const more = hits.length > 60 ? `\n… ${hits.length - 60} more` : ''
        if (hits.length) text = `URL: ${page.url}\nLines containing "${i.query}":\n${hits.slice(0, 60).join('\n')}${more}`
        else text = `URL: ${page.url}\nNothing on the page contains "${i.query}".`
        return { text, error: false, page: null }
      }
      return { text: pageToText(page), error: false, page: null }
    }
    default:
      throw new Error(`Unknown action ${a.name}`)
    }
  } catch (e) {
    error = true
    text = `Failed: ${e && e.message ? e.message : e}`
  }
  const page = snapshotPage(router)
  return { text, error, page }
}

// ── Plan du site donné à Claude (menu + pages d'ajout / de consultation) ─────

const EXTRA_ROUTES = [
  ['/person-add', 'Contacts > Add a contact (client, supplier, transporter; company or person). The list has a search box; a row opens the contact.'],
  ['/person/:id', 'Contact file (view): addresses, operations, documents'],
  ['/person-edit/:id', 'Edit a contact'],
  ['/quotation-add', 'Quotations > New quotation (pick the client, add product lines, save)'],
  ['/quotation-add-idpers/:personId', 'New quotation for a given contact'],
  ['/quotation/:id', 'Quotation view (PDF, mail, invoice buttons)'],
  ['/quotation-edit/:id', 'Edit a quotation'],
  ['/invoice-add', 'Invoices > New invoice'],
  ['/invoice/:id', 'Invoice view'],
  ['/invoice-edit/:id', 'Edit an invoice'],
  ['/sale-add', 'Sales > New sale'],
  ['/purchase-add', 'Purchases > New purchase'],
  ['/reception-add', 'Receptions > New reception (wood received)'],
  ['/production-add', 'Production > New production'],
  ['/inventory-add', 'Inventory > New inventory'],
  ['/transport-add', 'Transport > New outgoing transport'],
  ['/incoming-transport-add', 'Transport > New incoming transport'],
  ['/outlay-add', 'Expenses > New expense'],
  ['/product/product-list', 'Products catalogue (list, search)'],
  ['/stock/:productid', 'Stock of one product'],
  ['/employees/employees-list', 'Employees list'],
  ['/apv-encode', 'APV contracts to encode (Harvesting)'],
  ['/settings/menu', 'Settings menu (admin)'],
  ['/settings/ai', 'Settings > AI (Claude key)'],
  ['/reports/centralizator', 'Reports > Centralizator'],
  ['/kpi', 'KPI dashboard']
]

export function buildSitemap () {
  const lines = []
  const seen = new Set()
  const add = (path, label) => { if (path && !seen.has(path)) { seen.add(path); lines.push(`${path} → ${label}`) } }
  const walkItems = (items, prefix) => (items || []).forEach(it => {
    if (it.header) { walkItems(it.items, it.header); return }
    const label = [prefix, it.name].filter(Boolean).join(' > ')
    if (it.url) add(it.url, label)
    if (it.submenu) walkItems(it.submenu, label)
  })
  walkItems(navMenuItems, '')
  EXTRA_ROUTES.forEach(([p, l]) => add(p, l))
  return lines.join('\n')
}
